/**
 * Theme Helper Utilities
 * Centralized functions for theme-aware styling and color management
 */

import { Chart } from 'chart.js';
import { ThemeType } from '../styles/theme';
import { TYPOGRAPHY_CONFIG } from '../config/typography.config';
import { getMobileChartOverrides, isMobileChartViewport } from './chartResponsive';

// No custom type declarations needed

/**
 * Generate chart colors dynamically from CSS variables
 * This ensures colors always match the current theme in themes-unified.css
 */
const generateChartColorsFromCSS = (): string[] => {
  const colors: string[] = [];

  // Generate CSS variable references for all chart colors (1-11)
  for (let i = 1; i <= 11; i++) {
    colors.push(`var(--chart-color-${i})`);
  }

  return colors;
};

// Theme-agnostic chart colors using CSS variables - dynamically generated
export const CHART_COLORS = {
  // All themes use the same CSS variable references
  // The actual colors are defined in themes-unified.css and change based on active theme
  default: generateChartColorsFromCSS(),
  dark: generateChartColorsFromCSS(),
  light: generateChartColorsFromCSS(),
};

// Theme-agnostic chart gradients using CSS variables
export const CHART_GRADIENTS = {
  default: {
    primary: 'var(--gradient-primary, linear-gradient(135deg, var(--chart-color-1, #06B6D4) 0%, var(--chart-color-1-dark, #0891B2) 100%))',
    secondary: 'var(--gradient-secondary, linear-gradient(135deg, var(--chart-color-2, #8B5CF6) 0%, var(--chart-color-2-dark, #7C3AED) 100%))',
    success: 'var(--gradient-success, linear-gradient(135deg, var(--chart-color-3, #10B981) 0%, var(--chart-color-3-dark, #059669) 100%))',
    warning: 'var(--gradient-warning, linear-gradient(135deg, var(--chart-color-4, #F59E0B) 0%, var(--chart-color-4-dark, #D97706) 100%))',
    error: 'var(--gradient-error, linear-gradient(135deg, var(--chart-color-5, #EF4444) 0%, var(--chart-color-5-dark, #DC2626) 100%))',
  },

  // Backwards compatibility
  dark: {
    primary: 'var(--gradient-primary-dark, linear-gradient(135deg, #06B6D4 0%, #0891B2 100%))',
    secondary: 'var(--gradient-secondary-dark, linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%))',
    success: 'var(--gradient-success-dark, linear-gradient(135deg, #10B981 0%, #059669 100%))',
    warning: 'var(--gradient-warning-dark, linear-gradient(135deg, #F59E0B 0%, #D97706 100%))',
    error: 'var(--gradient-error-dark, linear-gradient(135deg, #EF4444 0%, #DC2626 100%))',
  },

  light: {
    primary: 'var(--gradient-primary-light, linear-gradient(135deg, #0891B2 0%, #0E7490 100%))',
    secondary: 'var(--gradient-secondary-light, linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%))',
    success: 'var(--gradient-success-light, linear-gradient(135deg, #059669 0%, #047857 100%))',
    warning: 'var(--gradient-warning-light, linear-gradient(135deg, #D97706 0%, #B45309 100%))',
    error: 'var(--gradient-error-light, linear-gradient(135deg, #DC2626 0%, #B91C1C 100%))',
  },
};

// Heatmap color mapping
export const HEATMAP_COLORS = {
  excellent: 'var(--heatmap-excellent)',
  good: 'var(--heatmap-good)',
  suitable: 'var(--heatmap-suitable)',
  average: 'var(--heatmap-average)',
  poor: 'var(--heatmap-poor)',
  bad: 'var(--heatmap-bad)',
  critical: 'var(--heatmap-critical)',
  noData: 'var(--heatmap-no-data)',
};

// Performance thresholds for heatmap colors
export const HEATMAP_THRESHOLDS = {
  excellent: 15,
  good: 10,
  suitable: 5,
  average: 2,
  poor: -5,
  bad: -10,
  critical: -15,
};

/**
 * Get chart colors (theme-agnostic approach)
 */
export const getChartColors = (theme?: ThemeType): string[] => {
  // Beautiful pastel colors that work well in both themes
  const fallbackColors = {
    dark: [
      '#BCFF08',
      '#FF6236',
      '#206387',
      '#FFD928',
      '#3CAC56',
      '#6860A4',
      '#5EDF6A',
      '#FFF5BC',
      '#30A89E',
      '#E94524',
      '#A8A8A8',
    ],
    light: [
      '#BCFF08',
      '#FF6236',
      '#206387',
      '#FFD928',
      '#3CAC56',
      '#6860A4',
      '#5EDF6A',
      '#FFF5BC',
      '#30A89E',
      '#E94524',
      '#A8A8A8',
    ]
  };

  // Always use fallback colors directly based on theme
  // This ensures consistent color display across all charts
  const selectedTheme = theme || 'dark';
  return fallbackColors[selectedTheme] || fallbackColors.dark;
};

/**
 * Get a specific chart color by index (theme-agnostic)
 * @deprecated Use getChartColors(theme)[index] directly instead
 */
export const getChartColor = (index: number, theme?: ThemeType): string => {
  const colors = getChartColors(theme);
  return colors[index % colors.length];
};

/**
 * Get chart background color with proper opacity
 */
export const getChartBackgroundColor = (index: number, theme: ThemeType = 'dark', opacity: number = 0.15): string => {
  const colors = getChartColors(theme);
  const color = colors[index % colors.length];

  // Handle CSS variables first to ensure they work properly
  if (color.includes('var(--')) {
    // Extract the variable name
    const varMatch = color.match(/var\((--[\w-]+)(?:,\s*([^)]+))?\)/);
    if (varMatch) {
      const varName = varMatch[1];
      const fallback = varMatch[2] || '';

      // For browser environments, get the computed value
      if (typeof window !== 'undefined' && document.documentElement) {
        const computedColor = getComputedStyle(document.documentElement).getPropertyValue(varName).trim() || fallback;
        if (computedColor) {
          // Now process the computed color
          if (computedColor.startsWith('#')) {
            const hex = computedColor.replace('#', '');
            const r = parseInt(hex.substr(0, 2), 16);
            const g = parseInt(hex.substr(2, 2), 16);
            const b = parseInt(hex.substr(4, 2), 16);
            return `rgba(${r}, ${g}, ${b}, ${opacity})`;
          } else if (computedColor.startsWith('rgb(')) {
            return computedColor.replace('rgb(', 'rgba(').replace(')', `, ${opacity})`);
          }
        }
      }

      // If fallback needed, use pastel colors based on index
      const fallbackColors = theme === 'light' ? [
        'rgba(56, 189, 248, ' + opacity + ')',  // bright sky blue
        'rgba(167, 139, 250, ' + opacity + ')', // bright lavender
        'rgba(74, 222, 128, ' + opacity + ')',  // bright mint green
        'rgba(250, 204, 21, ' + opacity + ')',  // bright yellow
        'rgba(248, 113, 113, ' + opacity + ')', // bright coral
        'rgba(129, 140, 248, ' + opacity + ')', // bright periwinkle
        'rgba(244, 114, 182, ' + opacity + ')', // bright rose
        'rgba(163, 230, 53, ' + opacity + ')',  // bright lime
        'rgba(251, 146, 60, ' + opacity + ')',  // bright orange
        'rgba(192, 132, 252, ' + opacity + ')', // bright purple
        'rgba(45, 212, 191, ' + opacity + ')',  // bright turquoise
        'rgba(251, 113, 133, ' + opacity + ')'  // bright pink
      ] : [
        'rgba(125, 211, 252, ' + opacity + ')', // sky blue pastel
        'rgba(196, 181, 253, ' + opacity + ')', // lavender pastel
        'rgba(134, 239, 172, ' + opacity + ')', // mint green pastel
        'rgba(253, 230, 138, ' + opacity + ')', // pale yellow pastel
        'rgba(252, 165, 165, ' + opacity + ')', // coral pink pastel
        'rgba(165, 180, 252, ' + opacity + ')', // periwinkle pastel
        'rgba(251, 207, 232, ' + opacity + ')', // rose pastel
        'rgba(190, 242, 100, ' + opacity + ')', // lime green pastel
        'rgba(254, 215, 170, ' + opacity + ')', // peach pastel
        'rgba(233, 213, 255, ' + opacity + ')', // light purple pastel
        'rgba(153, 246, 228, ' + opacity + ')', // turquoise pastel
        'rgba(254, 202, 202, ' + opacity + ')'  // light pink pastel
      ];

      return fallbackColors[index % fallbackColors.length];
    }
  }

  // Convert hex color to rgba if needed
  if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  // If already rgba, return as is
  if (color.startsWith('rgba')) {
    return color;
  }

  // If rgb, convert to rgba
  if (color.startsWith('rgb')) {
    return color.replace('rgb', 'rgba').replace(')', `, ${opacity})`);
  }

  // Fallback to a color-mix approach if all else fails
  return `color-mix(in srgb, ${color} ${Math.round(opacity * 100)}%, transparent)`;
};

/**
 * Get heatmap color based on performance difference
 * Supports both regular metrics (higher is better) and inverse metrics (lower is better)
 */
export const getHeatmapColor = (
  diffValue: number | null,
  metricType?: string,
  isInverse: boolean = false
): string => {
  if (diffValue === null || diffValue === undefined || isNaN(diffValue)) {
    return HEATMAP_COLORS.noData;
  }

  const diff = Number(diffValue);

  // For inverse metrics (like "days without sales"), lower values are better
  if (isInverse || metricType === 'تعداد_روز_بدون_فروش') {
    if (diff < -HEATMAP_THRESHOLDS.excellent) return HEATMAP_COLORS.excellent; // Much better
    if (diff < -HEATMAP_THRESHOLDS.good) return HEATMAP_COLORS.good; // Very good
    if (diff < -HEATMAP_THRESHOLDS.average) return HEATMAP_COLORS.average; // Good
    if (diff >= -HEATMAP_THRESHOLDS.average && diff <= HEATMAP_THRESHOLDS.average) {
      return HEATMAP_COLORS.average; // Neutral/average
    }
    if (diff > HEATMAP_THRESHOLDS.average && diff <= HEATMAP_THRESHOLDS.good) {
      return HEATMAP_COLORS.poor; // Below average
    }
    if (diff > HEATMAP_THRESHOLDS.good && diff <= HEATMAP_THRESHOLDS.excellent) {
      return HEATMAP_COLORS.poor; // Poor
    }
    return HEATMAP_COLORS.critical; // Very poor
  }

  // For regular metrics (higher values are better)
  if (diff > HEATMAP_THRESHOLDS.excellent) return HEATMAP_COLORS.excellent; // عالی (+15%)
  if (diff > HEATMAP_THRESHOLDS.good) return HEATMAP_COLORS.good; // خوب (+10%)
  if (diff > HEATMAP_THRESHOLDS.suitable) return HEATMAP_COLORS.suitable; // مناسب (+5%)
  if (diff >= -HEATMAP_THRESHOLDS.average && diff <= HEATMAP_THRESHOLDS.suitable) return HEATMAP_COLORS.average; // متوسط (±2%)
  if (diff >= HEATMAP_THRESHOLDS.poor && diff < -HEATMAP_THRESHOLDS.average) return HEATMAP_COLORS.poor; // ضعیف (-5%)
  if (diff >= HEATMAP_THRESHOLDS.bad && diff < HEATMAP_THRESHOLDS.poor) return HEATMAP_COLORS.bad; // نامناسب (-10%)
  return HEATMAP_COLORS.critical; // بحرانی (-15%)
};

/**
 * Get CSS variable value for the current theme
 */
export const getCSSVariable = (variableName: string): string => {
  if (typeof window !== 'undefined') {
    return getComputedStyle(document.documentElement)
      .getPropertyValue(variableName)
      .trim();
  }
  return '';
};

/**
 * Get intent color based on variant
 */
export const getIntentColor = (variant: 'success' | 'warning' | 'error' | 'info'): string => {
  switch (variant) {
    case 'success':
      return 'var(--intent-success)';
    case 'warning':
      return 'var(--intent-warning)';
    case 'error':
      return 'var(--intent-error)';
    case 'info':
      return 'var(--intent-info)';
    default:
      return 'var(--content-primary)';
  }
};

/**
 * Get intent background color based on variant
 */
export const getIntentBackgroundColor = (variant: 'success' | 'warning' | 'error' | 'info'): string => {
  switch (variant) {
    case 'success':
      return 'var(--intent-success-bg)';
    case 'warning':
      return 'var(--intent-warning-bg)';
    case 'error':
      return 'var(--intent-error-bg)';
    case 'info':
      return 'var(--intent-info-bg)';
    default:
      return 'var(--surface-secondary)';
  }
};

/**
 * Get intent border color based on variant
 */
export const getIntentBorderColor = (variant: 'success' | 'warning' | 'error' | 'info'): string => {
  switch (variant) {
    case 'success':
      return 'var(--intent-success-border)';
    case 'warning':
      return 'var(--intent-warning-border)';
    case 'error':
      return 'var(--intent-error-border)';
    case 'info':
      return 'var(--intent-info-border)';
    default:
      return 'var(--border-default)';
  }
};

/**
 * Merge chart options while preserving enhanced legend interactions
 * Use this instead of spread operators when overriding legend properties
 */
export const mergeChartOptions = (baseOptions: any, overrideOptions: any = {}) => {
  const merged = { ...baseOptions, ...overrideOptions };

  // Ensure legend handlers are preserved when plugins.legend is overridden
  if (overrideOptions.plugins?.legend && baseOptions.plugins?.legend) {
    merged.plugins.legend = {
      ...baseOptions.plugins.legend,
      ...overrideOptions.plugins.legend,
      // Always preserve the enhanced interaction handlers
      onHover: baseOptions.plugins.legend.onHover,
      onLeave: baseOptions.plugins.legend.onLeave,
      onClick: baseOptions.plugins.legend.onClick
    };
  }

  if (overrideOptions.layout && baseOptions.layout) {
    merged.layout = {
      ...baseOptions.layout,
      ...overrideOptions.layout,
    };
  }

  if (overrideOptions.scales) {
    merged.scales = { ...(baseOptions.scales || {}) };
    Object.keys(overrideOptions.scales).forEach((scaleKey) => {
      merged.scales[scaleKey] = {
        ...(baseOptions.scales?.[scaleKey] || {}),
        ...overrideOptions.scales[scaleKey],
      };
    });
  }

  return merged;
};

/**
 * Generate chart configuration with theme-aware colors
 */
export const getChartConfig = (theme: ThemeType = 'dark') => {
  const colors = getChartColors(theme);
  const isDarkTheme = theme !== 'light';

  return {
    colors: colors,
    grid: {
      color: isDarkTheme ? 'var(--border-default)' : '#F3F4F6',
    },
    text: {
      color: 'var(--content-secondary)',
      fontFamily: 'var(--font-primary)',
    },
    tooltip: {
      backgroundColor: 'var(--surface-elevated)',
      titleColor: 'var(--content-primary)',
      bodyColor: 'var(--content-secondary)',
      borderColor: 'var(--border-default)',
      borderWidth: 1,
      cornerRadius: 8,
    },
    legend: {
      labels: {
        color: 'var(--content-secondary)',
        usePointStyle: true,
        fontFamily: 'var(--font-primary)',
      },
    },
  };
};

/**
 * Format metric values with appropriate units and localization
 */
export const formatMetricValue = (
  value: number | null,
  metricType: 'percent' | 'currency' | 'count' | 'number' = 'number',
  locale: string = 'fa-IR'
): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '-';
  }

  switch (metricType) {
    case 'percent':
      return `${value.toFixed(1)}%`;
    case 'currency':
      return new Intl.NumberFormat(locale).format(Math.round(value));
    case 'count':
      return value.toFixed(0);
    case 'number':
    default:
      return new Intl.NumberFormat(locale).format(Math.round(value));
  }
};

/**
 * Generate CSS class names based on conditions
 */
export const classNames = (...classes: (string | undefined | null | false)[]): string => {
  return classes.filter(Boolean).join(' ');
};

/**
 * Get responsive grid classes based on item count
 */
export const getResponsiveGridClasses = (itemCount: number): string => {
  if (itemCount <= 2) {
    return 'grid-cols-1 sm:grid-cols-2';
  } else if (itemCount <= 3) {
    return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
  } else {
    return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';
  }
};

/**
 * Get theme-aware select styles for react-select
 */
export const getReactSelectStyles = () => ({
  control: (base: any, state: any) => ({
    ...base,
    backgroundColor: 'var(--surface-elevated)',
    borderColor: state.isFocused ? 'var(--border-focus)' : 'var(--border-default)',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(139, 92, 246, 0.2)' : 'none',
    '&:hover': {
      borderColor: 'var(--border-interactive)',
    },
    minHeight: '44px', // WCAG accessibility
    transition: 'var(--transition-colors)',
  }),
  menu: (base: any) => ({
    ...base,
    backgroundColor: 'var(--surface-elevated)',
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-lg)',
    zIndex: 9999,
  }),
  menuList: (base: any) => ({
    ...base,
    maxHeight: '200px',
    padding: '4px',
  }),
  option: (base: any, { isFocused, isSelected }: any) => ({
    ...base,
    backgroundColor: isSelected
      ? 'var(--color-interactive-primary)'
      : isFocused
        ? 'var(--surface-secondary)'
        : 'var(--surface-elevated)',
    color: isSelected ? 'var(--content-inverse)' : 'var(--content-primary)',
    '&:hover': {
      backgroundColor: 'var(--surface-secondary)',
    },
    fontSize: 'var(--text-sm)',
    padding: '8px 12px',
    cursor: 'pointer',
    transition: 'var(--transition-colors)',
  }),
  singleValue: (base: any) => ({
    ...base,
    color: 'var(--content-primary)',
    fontSize: 'var(--text-sm)',
  }),
  placeholder: (base: any) => ({
    ...base,
    color: 'var(--content-placeholder)',
    fontSize: 'var(--text-sm)',
  }),
  input: (base: any) => ({
    ...base,
    color: 'var(--content-primary)',
  }),
});

/**
 * Format chart value with appropriate unit
 */
export const formatChartValue = (value: any, unit?: string): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '-';
  }

  const numValue = typeof value === 'number' ? value : parseFloat(value);

  switch (unit) {
    case 'currency':
    case 'تومان':
      return new Intl.NumberFormat('fa-IR').format(numValue) + ' تومان';
    case 'ریال':
      return new Intl.NumberFormat('fa-IR').format(numValue) + ' ریال';
    case 'percent':
    case 'درصد':
    case '%':
      return numValue.toFixed(1) + '%';
    case 'count':
    case 'تعداد':
      return new Intl.NumberFormat('fa-IR').format(Math.round(numValue));
    case 'عدد':
      return new Intl.NumberFormat('fa-IR').format(Math.round(numValue)) + ' عدد';
    default:
      return new Intl.NumberFormat('fa-IR').format(numValue) + (unit ? ` ${unit}` : '');
  }
};

/**
 * Get theme-agnostic Chart.js options with enhanced tooltips
 */
export const getChartJsOptions = (theme?: ThemeType, unit?: string) => {
  // Check if we have CSS variables with computed style
  const getComputedVariable = (varName: string, fallback: string): string => {
    return typeof window !== 'undefined' ?
      getComputedStyle(document.documentElement).getPropertyValue(varName).trim() || fallback :
      fallback;
  };

  // Check for dark mode using CSS variables
  const isDarkMode = getComputedVariable('--is-dark-mode', 'true') === 'true';

  // No custom event listeners needed as we're using native Chart.js tooltips

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
    animation: {
      duration: 800,
      easing: 'easeInOutQuart' as const,
    },
    scales: {
      x: {
        grid: {
          display: false, // Remove gridlines
        },
        ticks: {
          color: 'var(--chart-axis-color, var(--text-primary))',
          // font: {
          //   family: 'var(--font-primary)',
          //   size: TYPOGRAPHY_CONFIG.components.chart.axisTick.size, // 16px - Default body text size
          //   weight: TYPOGRAPHY_CONFIG.components.chart.axisTick.weight, // Make labels bolder
          // },
          maxRotation: 0,
          padding: 12,
        },
        title: {
          font: {
            size: TYPOGRAPHY_CONFIG.components.chart.axisLabel.size,
            weight: TYPOGRAPHY_CONFIG.components.chart.axisLabel.weight,
            family: 'var(--font-primary)',
          },
        },
        border: {
          color: 'var(--chart-axis-border-color, var(--border-primary))',
          width: 2, // Make axis line bolder
        },
      },
      y: {
        grid: {
          display: false, // Remove gridlines
        },
        ticks: {
          color: 'var(--chart-axis-color, var(--text-primary))',
          // font: {
          //   family: 'var(--font-primary)',
          //   size: TYPOGRAPHY_CONFIG.components.chart.axisTick.size, // 16px - Default body text size
          //   weight: TYPOGRAPHY_CONFIG.components.chart.axisTick.weight, // Make labels bolder
          // },
          padding: 12,
          callback: function (value: any) {
            return formatChartValue(value, unit);
          },
        },
        title: {
          font: {
            size: TYPOGRAPHY_CONFIG.components.chart.axisLabel.size,
            weight: 700,
            family: 'var(--font-primary)',
          },
        },
        border: {
          color: 'var(--chart-axis-border-color, var(--border-primary))',
          width: 2, // Make axis line bolder
        },
      },
    },
    plugins: {
      legend: {
        position: 'bottom' as const,
        align: 'start' as const,
        rtl: true,
        labels: {
          color: 'var(--chart-legend-color, var(--text-primary))',
          usePointStyle: true,
          pointStyle: 'circle',
          textAlign: 'right' as const,
          font: {
            // family: 'var(--font-primary)',
            size: TYPOGRAPHY_CONFIG.components.chart.legend.size, // 18px - now using config
            weight: TYPOGRAPHY_CONFIG.components.chart.legend.weight, // Make legend bolder
          },
          padding: 30, // More padding for better spacing
          // Remove generateLabels for now to avoid errors
          // The visual feedback will be provided through CSS and hover states
        },
        // Add hover highlighting functionality
        onHover: function (event: any, legendItem: any, legend: any) {
          // Set cursor to pointer to indicate clickability
          const canvasElement = legend.chart.canvas;
          if (canvasElement) {
            canvasElement.style.cursor = 'pointer';
          }

          // Show tooltip for legend interaction
          let chartContainer = canvasElement.closest('.chart-container, .line-chart-container, .mixed-chart-container');

          if (!chartContainer) {
            chartContainer = canvasElement.closest('div[class*="chart"], div[class*="Chart"]');
          }

          if (!chartContainer) {
            chartContainer = canvasElement.closest('div');
            let currentElement = chartContainer;
            while (currentElement && currentElement !== document.body) {
              if (currentElement.offsetHeight > 200 && currentElement.offsetWidth > 200) {
                chartContainer = currentElement;
                break;
              }
              currentElement = currentElement.parentElement;
            }
          }

          if (!chartContainer) {
            chartContainer = canvasElement.parentElement;
          }

          if (chartContainer && !chartContainer.querySelector('.legend-tooltip')) {
            const tooltip = document.createElement('div');
            tooltip.className = 'legend-tooltip';
            tooltip.textContent = '💡 کلیک روی عناوین = مخفی/نمایش';
            tooltip.style.cssText = `
              position: absolute;
              top: -8px;
              right: 16px;
              background: var(--surface-elevated, rgba(255, 255, 255, 0.95));
              color: var(--content-secondary, rgba(107, 114, 128, 1));
              padding: 4px 12px;
              border-radius: 12px;
              font-size: 12px;
              font-weight: 500;
              opacity: 1;
              transition: all 0.3s ease;
              pointer-events: none;
              z-index: 10;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
              border: 1px solid var(--border-default, rgba(229, 231, 235, 1));
              backdrop-filter: blur(8px);
              white-space: nowrap;
              direction: rtl;
              text-align: right;
              transform: translateY(-4px);
            `;
            chartContainer.style.position = 'relative';
            chartContainer.appendChild(tooltip);
          }

          const chart = legend.chart;
          const datasetIndex = legendItem.datasetIndex;
          const isPieChart = chart.config.type === 'pie' || chart.config.type === 'doughnut';
          const isDarkMode = getComputedStyle(document.documentElement).getPropertyValue('--is-dark-mode')?.trim() === 'true';

          // Store original values if not already stored
          chart.data.datasets.forEach((dataset: any, index: number) => {
            if (!dataset.hasOwnProperty('originalBorderWidth')) {
              dataset.originalBorderWidth = dataset.borderWidth || 2;
            }
            if (!dataset.hasOwnProperty('originalPointRadius')) {
              dataset.originalPointRadius = dataset.pointRadius || 0;
            }
            if (!dataset.hasOwnProperty('originalPointHoverRadius')) {
              dataset.originalPointHoverRadius = dataset.pointHoverRadius || 5;
            }
            if (!dataset.hasOwnProperty('originalBackgroundColor')) {
              dataset.originalBackgroundColor = dataset.backgroundColor;
            }
            if (!dataset.hasOwnProperty('originalBorderColor')) {
              dataset.originalBorderColor = dataset.borderColor;
            }
          });

          // Highlight the hovered dataset
          chart.data.datasets.forEach((dataset: any, index: number) => {
            const meta = chart.getDatasetMeta(index);

            // For pie charts, handle differently since they have one dataset
            if (isPieChart && index === 0) {
              // For pie charts, legendItem.index represents the slice index
              const sliceIndex = legendItem.index;

              // Store original colors if not stored
              if (!dataset.originalBackgroundColors && Array.isArray(dataset.backgroundColor)) {
                dataset.originalBackgroundColors = [...dataset.backgroundColor];
              }

              // Keep original colors - no dimming
              if (Array.isArray(dataset.backgroundColor) && dataset.originalBackgroundColors) {
                dataset.backgroundColor = [...dataset.originalBackgroundColors];
              }

              // Apply visual effects - make highlighted section VERY bold and dramatic
              meta.data.forEach((arc: any, arcIndex: number) => {
                arc.options = arc.options || {};
                if (arcIndex === sliceIndex) {
                  // Highlight the selected section with its own color
                  const segmentColor = dataset.originalBorderColor?.[arcIndex] ||
                    dataset.borderColor?.[arcIndex] ||
                    dataset.originalBackgroundColors?.[arcIndex] ||
                    dataset.backgroundColor?.[arcIndex] ||
                    '#000000';

                  arc.options.borderWidth = 10; // Bold border
                  arc.options.borderColor = segmentColor; // Use the segment's own color
                  arc.options.offset = 30; // Pull out the segment
                  arc.options.hoverBorderWidth = 12; // Slightly thicker on direct hover
                  arc.options.hoverOffset = 35; // Pull out more on direct hover

                  // Add shadow effect (if supported)
                  arc.options.shadowColor = 'rgba(0, 0, 0, 0.3)';
                  arc.options.shadowBlur = 15;
                  arc.options.shadowOffsetX = 3;
                  arc.options.shadowOffsetY = 3;

                  // Scale up the highlighted section
                  arc.options.scale = 1.15; // Make it 15% bigger
                } else {
                  // Other sections - keep visible but clearly not selected
                  arc.options.borderWidth = 1;
                  arc.options.borderColor = 'rgba(128, 128, 128, 0.3)'; // Gray border
                  arc.options.offset = 0;
                  arc.options.hoverBorderWidth = 2;
                  arc.options.hoverOffset = 5;

                  // Remove any shadow and scale
                  arc.options.shadowColor = 'transparent';
                  arc.options.shadowBlur = 0;
                  arc.options.shadowOffsetX = 0;
                  arc.options.shadowOffsetY = 0;
                  arc.options.scale = 1; // Normal size
                }
              });

              // Also update the dataset border to match
              if (Array.isArray(dataset.borderColor)) {
                dataset.borderColor = dataset.borderColor.map((color: string, i: number) => {
                  if (i === sliceIndex) {
                    // Use the same color as the segment
                    return dataset.originalBorderColor?.[i] ||
                      dataset.originalBackgroundColors?.[i] ||
                      color;
                  }
                  return color;
                });
              }

              // Update dataset border width
              if (Array.isArray(dataset.borderWidth)) {
                dataset.borderWidth = dataset.borderWidth.map((width: number, i: number) => {
                  if (i === sliceIndex) {
                    return 10; // Match the arc border width
                  }
                  return 1;
                });
              } else {
                // Create array if it doesn't exist
                const borderWidths = new Array(meta.data.length).fill(1);
                borderWidths[sliceIndex] = 10;
                dataset.borderWidth = borderWidths;
              }
            } else if (!isPieChart && index === datasetIndex) {
              // HIGHLIGHTED DATASET for line/bar charts
              dataset.borderWidth = 6; // Very bold line
              dataset.pointRadius = 8; // Large visible points
              dataset.pointHoverRadius = 10; // Even larger on hover
              dataset.pointBackgroundColor = dataset.borderColor; // Fill points with line color
              dataset.pointBorderColor = '#fff'; // White border on points
              dataset.pointBorderWidth = 2;

              // Ensure full opacity for highlighted dataset
              if (dataset.backgroundColor && typeof dataset.backgroundColor === 'string') {
                dataset.backgroundColor = dataset.originalBackgroundColor || dataset.backgroundColor;
              }
              if (dataset.borderColor && typeof dataset.borderColor === 'string') {
                dataset.borderColor = dataset.originalBorderColor || dataset.borderColor;
              }

              // For bar charts - make them stand out
              if (meta.type === 'bar') {
                meta.data.forEach((bar: any) => {
                  bar.options = bar.options || {};
                  bar.options.borderWidth = 4;
                  bar.options.borderColor = dataset.borderColor || dataset.backgroundColor;
                });
              }
            } else if (!isPieChart) {
              // NON-HIGHLIGHTED DATASETS for line/bar charts
              dataset.borderWidth = 1; // Thin line
              dataset.pointRadius = 0; // Hide points
              dataset.pointHoverRadius = 0; // Hide points even on hover

              // Make other datasets very transparent (10% opacity)
              if (dataset.backgroundColor) {
                if (typeof dataset.backgroundColor === 'string' && dataset.backgroundColor.includes('rgba')) {
                  dataset.backgroundColor = dataset.backgroundColor.replace(/[\d.]+\)/, '0.1)');
                } else if (typeof dataset.backgroundColor === 'string' && dataset.backgroundColor.includes('rgb')) {
                  dataset.backgroundColor = dataset.backgroundColor.replace('rgb', 'rgba').replace(')', ', 0.1)');
                } else if (Array.isArray(dataset.backgroundColor)) {
                  dataset.backgroundColor = dataset.backgroundColor.map((color: string) => {
                    if (color.includes('rgba')) {
                      return color.replace(/[\d.]+\)/, '0.1)');
                    } else if (color.includes('rgb')) {
                      return color.replace('rgb', 'rgba').replace(')', ', 0.1)');
                    }
                    return color;
                  });
                }
              }

              if (dataset.borderColor) {
                if (typeof dataset.borderColor === 'string' && dataset.borderColor.includes('rgba')) {
                  dataset.borderColor = dataset.borderColor.replace(/[\d.]+\)/, '0.1)');
                } else if (typeof dataset.borderColor === 'string' && dataset.borderColor.includes('rgb')) {
                  dataset.borderColor = dataset.borderColor.replace('rgb', 'rgba').replace(')', ', 0.1)');
                }
              }

              // For bar charts - make them very transparent
              if (meta.type === 'bar') {
                meta.data.forEach((bar: any) => {
                  bar.options = bar.options || {};
                  bar.options.borderWidth = 0;
                  const currentBgColor = bar.options.backgroundColor || dataset.backgroundColor;
                  if (currentBgColor && typeof currentBgColor === 'string') {
                    if (currentBgColor.includes('rgba')) {
                      bar.options.backgroundColor = currentBgColor.replace(/[\d.]+\)/, '0.1)');
                    } else if (currentBgColor.includes('rgb')) {
                      bar.options.backgroundColor = currentBgColor.replace('rgb', 'rgba').replace(')', ', 0.1)');
                    }
                  }
                });
              }

              // For pie/doughnut charts - make segments very dim
              if (meta.type === 'pie' || meta.type === 'doughnut') {
                meta.data.forEach((arc: any) => {
                  arc.options = arc.options || {};
                  arc.options.borderWidth = 0;
                  arc.options.offset = 0;
                });
              }
            }
          });

          chart.update('none'); // Update without animation for instant feedback
        },
        onLeave: function (event: any, legendItem: any, legend: any) {
          // Reset cursor when leaving legend area
          const canvasElement = legend.chart.canvas;
          if (canvasElement) {
            canvasElement.style.cursor = 'default';
          }

          // Remove tooltip using the same container detection logic
          let chartContainer = canvasElement.closest('.chart-container, .line-chart-container, .mixed-chart-container');

          if (!chartContainer) {
            chartContainer = canvasElement.closest('div[class*="chart"], div[class*="Chart"]');
          }

          if (!chartContainer) {
            chartContainer = canvasElement.closest('div');
            let currentElement = chartContainer;
            while (currentElement && currentElement !== document.body) {
              if (currentElement.offsetHeight > 200 && currentElement.offsetWidth > 200) {
                chartContainer = currentElement;
                break;
              }
              currentElement = currentElement.parentElement;
            }
          }

          if (!chartContainer) {
            chartContainer = canvasElement.parentElement;
          }

          if (chartContainer) {
            const tooltip = chartContainer.querySelector('.legend-tooltip');
            if (tooltip) {
              tooltip.remove();
            }
          }

          const chart = legend.chart;

          // Force reset all datasets to original state
          chart.data.datasets.forEach((dataset: any, index: number) => {
            const meta = chart.getDatasetMeta(index);

            // Ensure we have original values stored
            if (!dataset.hasOwnProperty('originalBorderWidth')) {
              dataset.originalBorderWidth = dataset.borderWidth || 2;
            }
            if (!dataset.hasOwnProperty('originalPointRadius')) {
              dataset.originalPointRadius = dataset.pointRadius || 0;
            }
            if (!dataset.hasOwnProperty('originalPointHoverRadius')) {
              dataset.originalPointHoverRadius = dataset.pointHoverRadius || 5;
            }
            if (!dataset.hasOwnProperty('originalBackgroundColor')) {
              dataset.originalBackgroundColor = dataset.backgroundColor;
            }
            if (!dataset.hasOwnProperty('originalBorderColor')) {
              dataset.originalBorderColor = dataset.borderColor;
            }

            // Force restore all properties
            dataset.borderWidth = dataset.originalBorderWidth;
            dataset.pointRadius = dataset.originalPointRadius;
            dataset.pointHoverRadius = dataset.originalPointHoverRadius;
            dataset.backgroundColor = dataset.originalBackgroundColor;
            dataset.borderColor = dataset.originalBorderColor;

            // Remove any hover-specific styling
            delete dataset.pointBackgroundColor;
            delete dataset.pointBorderColor;
            delete dataset.pointBorderWidth;

            // Reset dataset opacity to full
            if (dataset.backgroundColor && typeof dataset.backgroundColor === 'string') {
              dataset.backgroundColor = dataset.backgroundColor.replace(/rgba?\([^,]+,[^,]+,[^,]+,\s*[\d.]+\)/, dataset.originalBackgroundColor);
            }

            // For bar charts - force reset
            if (meta.type === 'bar') {
              meta.data.forEach((bar: any) => {
                if (bar && bar.options) {
                  bar.options.borderWidth = 0;
                  bar.options.backgroundColor = dataset.originalBackgroundColor;
                  delete bar.options.borderColor;
                  delete bar.options.hoverBorderWidth;
                  delete bar.options.hoverBackgroundColor;
                }
              });
            }

            // For pie/doughnut charts - force reset
            if (meta.type === 'pie' || meta.type === 'doughnut') {
              // Store original colors if not already stored
              if (!dataset.originalBackgroundColors && Array.isArray(dataset.backgroundColor)) {
                dataset.originalBackgroundColors = [...dataset.backgroundColor];
              }

              // Restore original background colors
              if (dataset.originalBackgroundColors && Array.isArray(dataset.originalBackgroundColors)) {
                dataset.backgroundColor = [...dataset.originalBackgroundColors];
              }

              // Restore original border properties
              dataset.borderColor = dataset.originalBorderColor;
              dataset.borderWidth = dataset.originalBorderWidth || 2;

              // Reset all arc properties
              meta.data.forEach((arc: any) => {
                if (arc && arc.options) {
                  // Clear all hover effects
                  arc.options.borderWidth = 0;
                  arc.options.offset = 0;
                  delete arc.options.borderColor;
                  delete arc.options.hoverBorderWidth;
                  delete arc.options.hoverOffset;
                  delete arc.options.shadowColor;
                  delete arc.options.shadowBlur;
                  delete arc.options.shadowOffsetX;
                  delete arc.options.shadowOffsetY;
                  delete arc.options.scale;
                  delete arc.options.hoverBackgroundColor;
                }
              });
            }
          });

          // Force update the chart immediately
          chart.update('none');

          // Additional cleanup after a small delay to ensure state is reset
          setTimeout(() => {
            chart.update('none');
          }, 50);
        },
        onClick: function (event: any, legendItem: any, legend: any) {
          // Add visual feedback for click interaction
          const chart = legend.chart;
          // Find any chart container type - try multiple selectors
          let chartContainer = chart.canvas.closest('.chart-container, .line-chart-container, .mixed-chart-container');

          // If not found, try parent elements with chart-related classes
          if (!chartContainer) {
            chartContainer = chart.canvas.closest('div[class*="chart"], div[class*="Chart"]');
          }

          // If still not found, search for elements containing canvas
          if (!chartContainer) {
            chartContainer = chart.canvas.closest('div');
            // Walk up the DOM tree to find a suitable container
            let currentElement = chartContainer;
            while (currentElement && currentElement !== document.body) {
              if (currentElement.offsetHeight > 200 && currentElement.offsetWidth > 200) {
                chartContainer = currentElement;
                break;
              }
              currentElement = currentElement.parentElement;
            }
          }

          // Final fallback to canvas parent
          if (!chartContainer) {
            chartContainer = chart.canvas.parentElement;
          }

          if (chartContainer) {
            // Add pulse animation class
            chartContainer.classList.add('legend-active');

            // Remove the class after animation completes
            setTimeout(() => {
              chartContainer.classList.remove('legend-active');
            }, 300);
          }

          // Call the default legend click behavior
          // This toggles the dataset visibility
          const datasetIndex = legendItem.datasetIndex;
          const meta = chart.getDatasetMeta(datasetIndex);

          // Toggle visibility
          meta.hidden = meta.hidden === null ? !chart.data.datasets[datasetIndex].hidden : null;

          // Update the chart
          chart.update();
        }
      },
      tooltip: {
        enabled: true, // Use built-in tooltip
        position: 'nearest' as const, // Better position for RTL and improved accuracy
        displayColors: true,
        caretPadding: 12, // Increased for better positioning
        xAlign: 'right' as const, // Force right alignment
        yAlign: 'center' as const, // Center vertically
        backgroundColor: function (context: any) {
          // Get theme-specific background color - will be overridden by CSS
          return isDarkMode ? 'rgba(17, 24, 39, 0.97)' : 'rgba(255, 255, 255, 0.97)';
        },
        titleColor: function (context: any) {
          return isDarkMode ? 'rgba(255, 255, 255, 1)' : 'rgba(15, 23, 42, 1)';
        },
        bodyColor: function (context: any) {
          return isDarkMode ? 'rgba(229, 231, 235, 1)' : 'rgba(51, 65, 85, 1)';
        },
        borderColor: function (context: any) {
          return isDarkMode ? 'rgba(99, 102, 241, 0.4)' : 'rgba(124, 58, 237, 0.3)';
        },
        borderWidth: 2, // Thicker border for better visibility
        padding: 20, // Increased padding for better readability
        titleAlign: 'left' as const,
        bodyAlign: 'right' as const,
        footerAlign: 'right' as const,
        rtl: true,
        textDirection: 'rtl' as const,
        titleFont: {
          family: 'var(--font-primary)',
          size: TYPOGRAPHY_CONFIG.components.chart.tooltip.title.size, // 16px - now using config
          weight: TYPOGRAPHY_CONFIG.components.chart.tooltip.title.weight as any
        },
        bodyFont: {
          family: 'var(--font-primary)',
          size: TYPOGRAPHY_CONFIG.components.chart.tooltip.body.size, // 14px - now using config
          weight: TYPOGRAPHY_CONFIG.components.chart.tooltip.body.weight as any
        },
        cornerRadius: 12, // Controlled by CSS
        boxPadding: 16, // Increased space between color box and text
        usePointStyle: true, // Uses point style for the color boxes
        boxWidth: 22, // Slightly larger color box
        boxHeight: 24, // Slightly larger color box
        callbacks: {
          // Format data for tooltip
          title: function (context: any) {
            // Title without emoji, proper RTL
            const label = context[0].label || '';
            return label;
          },
          beforeTitle: function () {
            // Add extra space at the top for better layout
            return '';
          },
          label: function (context: any) {
            const label = context.dataset.label || '';
            const value = context.parsed.y !== undefined ? context.parsed.y : context.raw;

            let formattedValue = '';

            // Format value based on context and data type
            if (typeof value === 'number') {
              // Format currencies with toman/rial suffix
              if (label.toLowerCase().includes('فروش') ||
                label.toLowerCase().includes('درآمد') ||
                label.toLowerCase().includes('هزینه') ||
                label.toLowerCase().includes('قیمت')) {
                formattedValue = `${new Intl.NumberFormat('fa-IR').format(value)} تومان`;
              }
              // Format percentages
              else if (label.toLowerCase().includes('درصد') ||
                label.toLowerCase().includes('%') ||
                (value >= 0 && value <= 100 &&
                  (label.toLowerCase().includes('رضایت') ||
                    label.toLowerCase().includes('نرخ')))) {
                formattedValue = `${new Intl.NumberFormat('fa-IR').format(value)}%`;
              }
              // Format counts/quantities
              else if (label.toLowerCase().includes('تعداد') ||
                label.toLowerCase().includes('شمارش') ||
                label.toLowerCase().includes('مقدار')) {
                formattedValue = new Intl.NumberFormat('fa-IR').format(value);
              }
              // Default number formatting
              else {
                formattedValue = new Intl.NumberFormat('fa-IR').format(value);
              }
            } else {
              // Handle non-numeric values
              formattedValue = value?.toString() || '';
            }

            // Format with RTL alignment - label first, then value
            const separator = '\u00A0:\u00A0'; // Non-breaking space around colon

            // Check if label starts with English letters or digits
            const startsWithLatin = /^[a-zA-Z0-9]/.test(label);

            if (startsWithLatin) {
              // For labels starting with English/digits, use LTR embedding
              const ltrEmbed = '\u202D'; // Left-to-right override
              const popDir = '\u202C'; // Pop directional formatting

              // Format: label + separator + value
              return `${ltrEmbed}${label}${popDir}${separator}${formattedValue}`;
            } else {
              // For pure Persian labels, RTL format: label + separator + value
              return `${label}${separator}${formattedValue}`;
            }
          },
          // afterLabel: function (context: any) {
          //   // Add growth indicators if we have dataset comparisons
          //   const datasets = context.chart.data.datasets;
          //   if (datasets.length > 1 && context.datasetIndex === 0 && datasets[1]) {
          //     const currentValue = context.parsed.y;
          //     const comparisonValue = datasets[1].data[context.dataIndex];

          //     if (currentValue && comparisonValue && !isNaN(currentValue) && !isNaN(comparisonValue)) {
          //       const diff = currentValue - comparisonValue;
          //       const percentChange = (diff / Math.abs(comparisonValue)) * 100;

          //       if (Math.abs(percentChange) > 0.5) { // Only show if change is significant
          //         const isGrowth = diff > 0;
          //         const icon = isGrowth ? '📈' : '📉';
          //         const sign = isGrowth ? '+' : '';
          //         // Format change indicator on single line with RTL
          //         return `${percentChange.toFixed(1)}${sign}%\u00A0:\u00A0تغییر\u00A0${icon}`;
          //       }
          //     }
          //   }
          //   return null;
          // },
          labelTextColor: function (context: any) {
            // Brighter colors for better readability
            return isDarkMode ? 'rgba(255, 255, 255, 0.98)' : 'rgba(31, 41, 55, 0.98)';
          },
          labelPointStyle: function (context: any) {
            // Use circle point style for better appearance
            return {
              pointStyle: 'circle' as const, // Type assertion to match Chart.js expectations
              rotation: 0
            };
          }
        }
      },
      elements: {
        point: {
          radius: 0, // Remove dots on lines by default
          hoverRadius: 8,
          hoverBorderWidth: 3,
          borderWidth: 2,
        },
        line: {
          tension: 0.4,
          borderCapStyle: 'round' as const,
          borderJoinStyle: 'round' as const,
        },
      }
    }
  };

  if (isMobileChartViewport()) {
    return mergeChartOptions(options, getMobileChartOverrides());
  }

  return options;
}

/**
 * Performance indicator helpers
 */
export const getPerformanceIndicator = (diffValue: number | null): {
  icon: string;
  color: string;
  label: string;
} => {
  if (diffValue === null || diffValue === undefined || isNaN(diffValue)) {
    return {
      icon: '→',
      color: 'var(--heatmap-no-data)',
      label: 'بدون داده',
    };
  }

  const diff = Number(diffValue);

  if (diff > 15) {
    return { icon: '↗↗', color: 'var(--heatmap-excellent)', label: 'عالی' };
  } else if (diff > 10) {
    return { icon: '↗', color: 'var(--heatmap-good)', label: 'خوب' };
  } else if (diff > 5) {
    return { icon: '↗', color: 'var(--heatmap-suitable)', label: 'مناسب' };
  } else if (diff >= -2) {
    return { icon: '→', color: 'var(--heatmap-average)', label: 'متوسط' };
  } else if (diff >= -5) {
    return { icon: '↘', color: 'var(--heatmap-poor)', label: 'ضعیف' };
  } else if (diff >= -10) {
    return { icon: '↘', color: 'var(--heatmap-bad)', label: 'نامناسب' };
  } else {
    return { icon: '↘↘', color: 'var(--heatmap-critical)', label: 'بحرانی' };
  }
};

/**
 * RTL-aware text direction utilities
 */
export const getTextDirection = (text: string): 'ltr' | 'rtl' => {
  // Simple heuristic: if text contains Persian/Arabic characters, use RTL
  const persianArabicRegex = /[\u0600-\u06FF]/;
  return persianArabicRegex.test(text) ? 'rtl' : 'ltr';
};

/**
 * Accessibility helpers
 */
export const getAriaLabel = (
  title: string,
  value: string | number,
  unit?: string,
  context?: string
): string => {
  const formattedValue = typeof value === 'number' ? value.toLocaleString('fa-IR') : value;
  const unitText = unit ? ` ${unit}` : '';
  const contextText = context ? ` در ${context}` : '';

  return `${title}: ${formattedValue}${unitText}${contextText}`;
};

/**
 * Format tooltip label with proper RTL support
 * @param label - The dataset label
 * @param value - The formatted value (with units)
 * @returns Properly formatted RTL tooltip string
 */
export const formatTooltipLabel = (label: string, value: string): string => {
  const separator = '\u00A0:\u00A0'; // Non-breaking space around colon

  // Check if label starts with English letters or digits
  const startsWithLatin = /^[a-zA-Z0-9]/.test(label);
  const ltrEmbed = '\u202D'; // Left-to-right override
  const popDir = '\u202C'; // Pop directional formatting

  if (startsWithLatin) {
    // For labels starting with English/digits, use LTR embedding
    // Format: label + separator + value
    return `${ltrEmbed}${label}${popDir}${separator}${value}`;
  } else {
    // For pure Persian labels, RTL format: label + separator + value
    return `${label}${separator}${value}`;
  }
};

const themeHelpers = {
  getChartColors,
  getChartColor,
  getChartBackgroundColor,
  getHeatmapColor,
  getCSSVariable,
  getIntentColor,
  getIntentBackgroundColor,
  getIntentBorderColor,
  getChartConfig,
  formatMetricValue,
  formatChartValue,
  classNames,
  getResponsiveGridClasses,
  getReactSelectStyles,
  getChartJsOptions,
  getPerformanceIndicator,
  getTextDirection,
  getAriaLabel,
  formatTooltipLabel,
};

/**
 * Wrap a custom tooltip label callback to ensure RTL formatting
 * Use this when you need to override the default tooltip label callback
 * @param customCallback - The custom callback function
 * @returns A wrapped callback that preserves RTL formatting
 */
export const wrapTooltipLabelCallback = (customCallback: (context: any) => string) => {
  return (context: any) => {
    const customLabel = customCallback(context);

    // If the custom label already includes RTL formatting (has non-breaking spaces and colon), return as is
    if (customLabel.includes('\u00A0:\u00A0')) {
      return customLabel;
    }

    // Otherwise, apply RTL formatting
    // Try to detect if there's a label:value pattern
    const colonIndex = customLabel.indexOf(':');
    if (colonIndex > -1) {
      const label = customLabel.substring(0, colonIndex).trim();
      const value = customLabel.substring(colonIndex + 1).trim();
      const separator = '\u00A0:\u00A0'; // Non-breaking space around colon

      // Check if label starts with English letters or digits
      const startsWithLatin = /^[a-zA-Z0-9]/.test(label);

      if (startsWithLatin) {
        // For labels starting with English/digits, use LTR embedding
        const ltrEmbed = '\u202D'; // Left-to-right override
        const popDir = '\u202C'; // Pop directional formatting

        // Format: label + separator + value
        return `${ltrEmbed}${label}${popDir}${separator}${value}`;
      } else {
        // For pure Persian labels, RTL format: label + separator + value
        return `${label}${separator}${value}`;
      }
    }

    // If no colon pattern, return the custom label as is
    return customLabel;
  };
};

export default themeHelpers;